<template>
  <div class="gm-page skill-calculator-page">
    <div class="background-layer"></div>

    <div class="gm-topbar">
      <div>
        <div class="gm-eyebrow">Game Master / Balance Tools</div>
        <h1 class="gm-title">Skill Balance Analyzer</h1>
        <p class="gm-subtitle">Analyze skill scaling across stat progression</p>
      </div>
    </div>

    <div class="gm-page__body gm-scroll">
      <div class="content-wrapper">
        <div class="gm-panel control-panel">
          <div class="gm-field control-group">
            <label class="gm-label">Skill</label>
            <SkillSelector
              v-model="selectedSkillId"
              :campaign-id="campaignId"
              placeholder="Select a skill to analyze..."
              @update:modelValue="handleSkillChange"
            />
          </div>

          <div class="gm-field control-group">
            <label class="gm-label">Primary Stat</label>
            <select v-model="primaryStat" class="gm-select" @change="analyzeSkill">
              <option v-for="stat in availableStats" :key="stat.value" :value="stat.value">
                {{ stat.label }}
              </option>
            </select>
          </div>

          <div class="gm-field control-group">
            <label class="gm-label">Impact Filter</label>
            <select v-model="impactFilter" class="gm-select" @change="updateChartData">
              <option value="ALL">All Impacts</option>
              <option v-for="impact in availableImpactTypes" :key="impact.value" :value="impact.value">
                {{ impact.label }}
              </option>
            </select>
          </div>

          <div class="gm-field control-group">
            <label class="gm-label">Range</label>
            <div class="range-inputs">
              <input
                v-model.number="minValue"
                type="number"
                min="0"
                max="300"
                class="gm-input range-input"
                @change="analyzeSkill"
              />
              <span class="range-separator">&rarr;</span>
              <input
                v-model.number="maxValue"
                type="number"
                min="0"
                max="300"
                class="gm-input range-input"
                @change="analyzeSkill"
              />
              <span class="range-label">Step:</span>
              <input
                v-model.number="step"
                type="number"
                min="1"
                max="50"
                class="gm-input step-input"
                @change="analyzeSkill"
              />
            </div>
          </div>

          <button
            @click="analyzeSkill"
            class="gm-btn gm-btn--primary analyze-btn"
            :disabled="!selectedSkillId || isAnalyzing"
          >
            <span v-if="isAnalyzing" class="dx-loader dx-loader--xs"></span>
            {{ isAnalyzing ? 'Analyzing...' : 'Analyze' }}
          </button>
        </div>

        <div class="chart-section">
          <SkillProgressionChart :chart-data="chartData" />
        </div>

        <div v-if="chartData" class="gm-panel legend-info">
          <div class="legend-item">
            <div class="legend-line dashed"></div>
            <span>Minimum (other stats = {{ baselineMinimum }})</span>
          </div>
          <div class="legend-item">
            <div class="legend-line solid"></div>
            <span>Median (other stats = {{ baselineMedian }})</span>
          </div>
          <div class="legend-item">
            <div class="legend-line dotted"></div>
            <span>Maximum (other stats = {{ baselineMaximum }})</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import SkillSelector from '@/components/Selectors/SkillSelector.vue';
import SkillProgressionChart from '@/components/GameMaster/SkillCalculator/SkillProgressionChart.vue';
import { gameMasterSkillProgressionService } from '@/services/GameMasterSkillProgressionService';
import { CharacterStatEnum, ImpactTypeEnum } from '@/api/dx-backend';

const store = useStore();

// Campaign
const campaignId = computed(() => store.state.selectedCampaign?.id);

// Form state
const selectedSkillId = ref(null);
const primaryStat = ref(CharacterStatEnum.FlowManipulation);
const impactFilter = ref('ALL');
const minValue = ref(0);
const maxValue = ref(150);
const step = ref(5);
const baselineMinimum = ref(10);
const baselineMedian = ref(50);
const baselineMaximum = ref(100);

// Analysis state
const isAnalyzing = ref(false);
const progressionData = ref(null);
const chartData = ref(null);
const error = ref(null);

// Available options from enums
const availableStats = Object.keys(CharacterStatEnum).map(key => ({
  value: CharacterStatEnum[key],
  label: CharacterStatEnum[key]
}));

const availableImpactTypes = Object.keys(ImpactTypeEnum)
  .filter(key => !['None', 'KnockOut'].includes(key))
  .map(key => ({
    value: ImpactTypeEnum[key],
    label: ImpactTypeEnum[key]
  }));

// Analyze skill progression
const analyzeSkill = async () => {
  if (!selectedSkillId.value) return;

  try {
    isAnalyzing.value = true;
    error.value = null;

    const result = await gameMasterSkillProgressionService.analyzeProgression({
      skill_id: selectedSkillId.value,
      primary_stat: primaryStat.value,
      min_value: minValue.value,
      max_value: maxValue.value,
      step: step.value,
      baseline_minimum: baselineMinimum.value,
      baseline_median: baselineMedian.value,
      baseline_maximum: baselineMaximum.value
    });

    progressionData.value = result;
    updateChartData();
  } catch (err) {
    console.error('Analysis failed:', err);
    error.value = err.message || 'Failed to analyze skill progression';
    chartData.value = null;
  } finally {
    isAnalyzing.value = false;
  }
};

// Update chart data with current filter
const updateChartData = () => {
  if (!progressionData.value) return;

  chartData.value = gameMasterSkillProgressionService.extractChartData(
    progressionData.value,
    impactFilter.value
  );
};

// Handle skill selection change
const handleSkillChange = () => {
  if (selectedSkillId.value) {
    analyzeSkill();
  } else {
    chartData.value = null;
  }
};
</script>

<style scoped>
.skill-calculator-page {
  position: relative;
}

.background-layer {
  position: fixed;
  inset: 0;
  background-image: url("@/assets/images/backgrounds/DALL·E 2024-12-21 20.36.35 - A futuristic, sleek background for a game interface representing the 'Path of John'. The scene includes vibrant shades of blue and silver with abstrac.webp");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  filter: grayscale(85%) brightness(0.3);
  opacity: 0.4;
  z-index: -2;
}

.background-layer::after {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--gm-surface-0);
  opacity: 0.82;
  z-index: -1;
}

.content-wrapper {
  position: relative;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--gm-gap-lg);
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-lg);
}

.control-panel {
  display: flex;
  gap: var(--gm-gap);
  align-items: flex-end;
  flex-wrap: wrap;
}

.control-group {
  flex: 1;
  min-width: 10rem;
}

.range-inputs {
  display: flex;
  gap: var(--gm-gap-sm);
  align-items: center;
}

.range-input {
  width: 4.5rem;
}

.step-input {
  width: 3.5rem;
}

.range-separator,
.range-label {
  font-size: 0.8rem;
  color: var(--gm-text-muted);
}

.analyze-btn {
  white-space: nowrap;
}

.chart-section {
  flex: 1;
  min-height: 500px;
  max-height: 600px;
}

.legend-info {
  display: flex;
  justify-content: center;
  gap: var(--gm-gap-lg);
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: var(--gm-text-muted);
}

.legend-line {
  width: 40px;
  height: 2px;
  background: var(--gm-accent);
}

.legend-line.dashed {
  background: linear-gradient(to right, var(--gm-accent) 50%, transparent 50%);
  background-size: 8px 2px;
}

.legend-line.solid {
  background: var(--gm-accent);
}

.legend-line.dotted {
  background: linear-gradient(to right, var(--gm-accent) 33%, transparent 33%);
  background-size: 4px 2px;
}

@media (max-width: 1024px) {
  .legend-info {
    flex-direction: column;
    gap: var(--gm-gap-sm);
  }
}
</style>
